import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import {
	chevronDown,
	chevronRight,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import styles from "./title-card-holder.module.css";

export type TitleCardItem = {
	readonly id: string;
	readonly title: string;
	readonly content?: ReactNode;
	readonly expandable?: boolean;
	readonly titleOnly?: boolean;
	readonly initiallyExpanded?: boolean;
};

export type TitleCardHolderProps = {
	readonly cards: readonly TitleCardItem[];
	readonly defaultExpandedId?: string;
	readonly onExpandedChange?: (id: string) => void;
	readonly className?: string;
};

function getInitialExpandedIds(
	cards: readonly TitleCardItem[],
	defaultExpandedId?: string,
): string[] {
	if (
		defaultExpandedId &&
		cards.some((card) => card.id === defaultExpandedId)
	) {
		return [defaultExpandedId];
	}

	const initiallyExpandedCards = cards.filter(
		(card) => card.initiallyExpanded && card.expandable !== false,
	);

	if (initiallyExpandedCards.length > 0) {
		return initiallyExpandedCards.map((card) => card.id);
	}

	const firstExpandableCard = cards.find((card) => card.expandable !== false);

	return firstExpandableCard ? [firstExpandableCard.id] : [];
}

export function TitleCardHolder({
	cards,
	defaultExpandedId,
	onExpandedChange,
	className,
}: Readonly<TitleCardHolderProps>) {
	const [expandedIds, setExpandedIds] = useState<string[]>(() =>
		getInitialExpandedIds(cards, defaultExpandedId),
	);

	useEffect(() => {
		setExpandedIds((previousExpandedIds) => {
			const validExpandedIds = previousExpandedIds.filter((id) =>
				cards.some((card) => card.id === id),
			);

			// Handles expanding cards that come in later,
			// outside the initial list of initially expanded cards.
			const newCards = cards.filter(
				(card) =>
					!validExpandedIds.includes(card.id) && card.expandable !== false,
			);

			validExpandedIds.push(...newCards.map((card) => card.id));

			if (validExpandedIds.length > 0) {
				return validExpandedIds;
			}

			return getInitialExpandedIds(cards, defaultExpandedId);
		});
	}, [cards, defaultExpandedId]);

	const holderClassName = useMemo(
		() => (className ? `${styles.container} ${className}` : styles.container),
		[className],
	);

	return (
		<div
			className={holderClassName}
			data-slot={"title-card-holder"}
			data-testid={"title-card-holder"}
		>
			{cards.map((card) => {
				const isExpanded = expandedIds.includes(card.id);
				const isExpandable = card.expandable !== false;
				const showContent = isExpanded && !card.titleOnly;
				const buttonTitle = isExpanded ? "collapse" : "expand";
				const icon = isExpanded ? chevronDown : chevronRight;

				return (
					<section
						key={card.id}
						className={styles.card}
						data-slot={"title-card"}
					>
						<button
							aria-controls={`title-card-panel-${card.id}`}
							aria-expanded={isExpandable ? isExpanded : undefined}
							className={`${styles.titleButton} ${isExpandable ? styles.clickable : ""}`}
							data-slot={"title-card-header"}
							data-testid={`title-card-${card.id}-toggle`}
							onClick={
								isExpandable
									? () => {
											setExpandedIds((previousExpandedIds) => {
												if (isExpanded) {
													return previousExpandedIds.filter(
														(id) => id !== card.id,
													);
												}

												return [...previousExpandedIds, card.id];
											});
											onExpandedChange?.(card.id);
										}
									: undefined
							}
							title={isExpandable ? buttonTitle : undefined}
							type={"button"}
						>
							<span className={styles.title} data-slot={"title-card-title"}>
								{card.title}
							</span>

							{isExpandable ? (
								<SvgIcon
									className={styles.chevron}
									data-slot={"title-card-chevron"}
									icon={icon}
									size={"large"}
								/>
							) : null}
						</button>

						{showContent ? (
							<div
								className={styles.content}
								data-slot={"title-card-content"}
								id={`title-card-panel-${card.id}`}
							>
								{card.content}
							</div>
						) : null}
					</section>
				);
			})}
		</div>
	);
}
